import { Modal } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import {
    AuthReducerProps,
    RoleProps,
} from "../../../store/reducers/authReducer";
import { validateEmail } from "../../../utilities/validateEmail";
import Input from "../../MUI/Input";
import SaveButton, { saveStatuses, saveTypes } from "../../MUI/SaveButton";
import Select, { SelectOptionProps } from "../../MUI/Select";
import { DataProps, UserProps } from "./Users";

import api from "../../../api/api";
import { useSelector } from "react-redux";
import Checkbox from "../../MUI/Checkbox";

interface Props {
    user: UserProps;
    roles: RoleProps[];
    closePopup: () => void;
    setData: React.Dispatch<React.SetStateAction<DataProps>>;
}

interface ErrorData {
    email: boolean;
    username: boolean;
    roleId: boolean;
    message: string;
}

const EditUser: React.FC<Props> = ({ user, roles, closePopup, setData }) => {
    const [status, setStatus] = useState<saveStatuses>(saveStatuses.NEUTRAL);
    const [username, setUsername] = useState<string>(user.username);
    const [email, setEmail] = useState<string>(user.email);
    const [isAllowedToAddCharacters, setIsAllowedToAddCharacters] =
        useState<boolean>(user.isAllowedToAddCharacters);
    const [roleId, setRoleId] = useState<string | null>(user.roleId);
    const [error, setError] = useState<ErrorData>({
        email: false,
        username: false,
        roleId: false,
        message: "",
    });

    const uid: string | null = useSelector(
        (state: AuthReducerProps) => state.uid,
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Enter" && status === saveStatuses.NEUTRAL) {
                setStatus(saveStatuses.SAVE_TRIGGERED);
            }
        },
        [status],
    );

    const handleRoleChanged = useCallback(
        (
            event: React.ChangeEvent<{
                name?: string | undefined;
                value: unknown;
            }>,
            value: any,
        ) => {
            let role = roles.find((r) => r.id === value);
            if (role) {
                setRoleId(role.id);
            }
        },
        [roles],
    );

    useEffect(() => {
        if (status === saveStatuses.SAVE_TRIGGERED) {
            // Set to saving
            setStatus(saveStatuses.SAVING);

            // Initialise error data
            let errorData: ErrorData = {
                email: false,
                username: false,
                roleId: false,
                message: "",
            };

            // Verify email address
            if (!validateEmail(email)) {
                errorData.email = true;
            }

            // Verify username
            if (username.trim().length < 2) {
                errorData.username = true;
            }

            if (roleId === null) {
                errorData.roleId = true;
            }

            // Verify form errors
            setError(errorData);
            if (errorData.email || errorData.username || errorData.roleId) {
                setStatus(saveStatuses.ERROR);
                return;
            }

            // Try to update
            api.put("user/admin/" + user.id, {
                id: user.id,
                username,
                email,
                roleId,
                isAllowedToAddCharacters,
            })
                .then((response) => {
                    setData((data) => {
                        let users = data.users.map((u) => {
                            if (u.id === user.id) {
                                return { ...response.data };
                            }

                            return u;
                        });

                        return {
                            ...data,
                            users: users,
                        };
                    });
                    closePopup();
                })
                .catch((error) => {
                    let message = "Server error";
                    if (error?.response?.data) {
                        message = error.response.data;
                    }

                    setStatus(saveStatuses.ERROR);
                    setError((currentError) => {
                        return {
                            ...currentError,
                            message: message,
                        };
                    });
                });
        }
    }, [
        uid,
        status,
        user,
        username,
        email,
        roleId,
        isAllowedToAddCharacters,
        closePopup,
        setData,
    ]);

    let options: SelectOptionProps[] = roles.map((role) => {
        return {
            value: role.id,
            label: role.name,
        };
    });

    return (
        <Modal
            open={true}
            onClose={status === saveStatuses.NEUTRAL ? closePopup : () => {}}
            aria-labelledby="update-account-popup"
            aria-describedby="Edit an account"
            className="d-flex align-items-center justify-content-center"
        >
            <div className="bg-white outline-0 oberin-popup">
                <div className="py-1 px-2 bg-dark-red text-white">
                    <span>{user.username}</span>
                </div>
                <div className="d-flex flex-column p-2">
                    <Input
                        id="create-account-popup-username"
                        label="Username"
                        value={username}
                        error={error.username && username.trim().length < 2}
                        helperText={
                            "The username must be a minimum length of 2"
                        }
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => setUsername(event.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <Input
                        id="create-account-popup-email"
                        label="Email"
                        value={email}
                        error={error.email && !validateEmail(email)}
                        helperText={"Please fill in a correct email"}
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => setEmail(event.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <Checkbox
                        label="Add characters"
                        checked={isAllowedToAddCharacters}
                        onChange={(event) =>
                            setIsAllowedToAddCharacters(event.target.checked)
                        }
                    />
                    <Select
                        id="create-account-popup-select-role"
                        label="Select role"
                        className={["w-100", "mt-2"]}
                        value={roleId}
                        options={options}
                        error={error.roleId && roleId === null}
                        helperText="Please select a role"
                        onChange={handleRoleChanged}
                        onKeyDown={handleKeyDown}
                    />

                    {error.message.trim() !== "" ? (
                        <div className="mt-2 mb-0 alert alert-danger rounded-0">
                            {error.message}
                        </div>
                    ) : null}

                    <div className="w-50 mt-2 ml-auto">
                        <SaveButton
                            type={saveTypes.CONTROLLED}
                            status={status}
                            setStatus={setStatus}
                            showFeedback
                            hideCancel
                            saveClasses={["dark-red-button"]}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default EditUser;
